// The two uniform structs the PBR passes read, packed. Pure — no WGSL import, no
// device — because these are byte offsets a shader reads blind: a field in the
// wrong slot is a plausible render with the wrong roughness or the wrong light,
// never an error.

import type { PbrConfig } from "../assets/pbrManifest.ts";

/** pbr_material.wgsl MatParams: 12 scalars, then three vec4 rows. */
export const MAT_PARAMS_BYTES = 96;
/** ogs_shade.wgsl Params: campos3, diffuseScale, specScale, P, fgRes, useNBSDF, specWeight, lit. */
export const SHADE_PARAMS_BYTES = 48;
/** pos3, nrm3, albedo3, roughness, specVis. */
export const MAT_STRIDE = 11;

/** The splat-local → world similarity. The light is a world-space SH probe and
 *  the lift works in the training frame the mesh is rotated out of. */
export interface LocalToWorld {
  /** Row-major 3x3, nine numbers. */
  readonly basis: ArrayLike<number>;
  readonly translation: ArrayLike<number>;
}

export const IDENTITY_LOCAL_TO_WORLD: LocalToWorld = Object.freeze({
  basis: Object.freeze([1, 0, 0, 0, 1, 0, 0, 0, 1]),
  translation: Object.freeze([0, 0, 0]),
});

/** What a consumer drives on a shade stage: the frame's camera and placement.
 *  Structural, so a wrapper can forward it and a stub needs no WebGPU. */
export interface PbrShadeLike {
  setFrame(camposWorld: ArrayLike<number>, localToWorld: LocalToWorld,
           scene?: { root: unknown; at: ArrayLike<number> }): void;
  /** The branch's PBR toggle. Present on the stage itself because a shade stage
   *  exists ONLY where the bundle declared a material — so "can this be toggled"
   *  and "is there anything to toggle" are the same question. */
  setLit(on: boolean): void;
  readonly isLit: boolean;
}

export function packMatParams(
  out: ArrayBuffer, pbr: PbrConfig, uvRes: number, hw: number, l2w: LocalToWorld,
): void {
  const u = new Uint32Array(out);
  const f = new Float32Array(out);
  const { channels } = pbr;
  u[0] = uvRes; u[1] = uvRes; u[2] = hw; u[3] = uvRes;
  u[4] = pbr.apprChannels;
  u[5] = pbr.featDim;
  u[6] = channels.albedo[0];
  u[7] = channels.roughness[0];
  u[8] = channels.specVis[0];
  u[9] = channels.feature[0];
  f[10] = pbr.minRoughness;
  f[11] = 0;
  // Row-major in, rows out — each row's translation component in .w.
  for (let row = 0; row < 3; row++) {
    const o = 12 + row * 4;
    f[o] = Number(l2w.basis[row * 3]);
    f[o + 1] = Number(l2w.basis[row * 3 + 1]);
    f[o + 2] = Number(l2w.basis[row * 3 + 2]);
    f[o + 3] = Number(l2w.translation[row]);
  }
}

export interface ShadeFrame {
  campos: ArrayLike<number>;
  /** Texel slots — P here is the lift's capacity, not the surviving splat count. */
  splats: number;
  fgRes: number;
  useNeuralBsdf: boolean;
  /** false renders the albedo instead of the radiance — the branch's PBR toggle. */
  lit: boolean;
}

export function packShadeParams(out: ArrayBuffer, frame: ShadeFrame): void {
  const f = new Float32Array(out);
  const u = new Uint32Array(out);
  f[0] = Number(frame.campos[0]);
  f[1] = Number(frame.campos[1]);
  f[2] = Number(frame.campos[2]);
  // The probe's band 0 back to the radiance python's env_diffuse returns.
  f[3] = 1 / Math.PI;
  // The basis constant already carries the specular scale.
  f[4] = 1;
  u[5] = frame.splats;
  u[6] = frame.fgRes;
  u[7] = frame.useNeuralBsdf ? 1 : 0;
  // 1 at inference; a checkpoint mid-curriculum is inspected at its own weight.
  f[8] = 1;
  u[9] = frame.lit ? 1 : 0;
}

/** Edge length of a square 2-channel FG table. Throws on anything else — a
 *  mis-sized table indexes the wrong roughness row and still renders. */
export function fgLutResolution(length: number): number {
  const res = Math.round(Math.sqrt(length / 2));
  if (res <= 0 || res * res * 2 !== length) {
    throw new Error(`PbrShade: FG LUT is ${length} floats, not a square x2 table`);
  }
  return res;
}

/** The 27 SH floats to light with: the probe when it is one, else the light the
 *  material was TRAINED under. Null only when the bundle ships neither. */
export function resolveLight(
  probe: ArrayLike<number> | null | undefined, trained: Float32Array | null,
): ArrayLike<number> | null {
  return probe && probe.length === 27 ? probe : trained;
}

/** Fractional SH move that earns a re-upload. Below it the light is unchanged
 *  for rendering purposes and the write is waste; hair gates its re-shade the
 *  same way (`lightProbeSHDrift`). */
export const RELIGHT_SH_DRIFT = 0.01;

export type LightSource = "scene" | "probe" | "scene+probe" | "trained" | "none";

export interface LightDecision {
  /** Upload this frame? */
  readonly upload: boolean;
  /** Which light is now in force. */
  readonly source: LightSource;
}

/** Whether the shade's light buffer needs rewriting, and with whose light.
 *
 *  The probe wins whenever the scene has one — the avatar walks through the
 *  grid, so it changes — and a probe that goes AWAY (the environment was
 *  switched, or its bake is missing) hands the light back to the bundle's own
 *  rather than freezing on the last one seen. */
export function nextLight(state: {
  /** The scene's own analytic lights carry energy — a torch, a sun, an ambient. */
  hasScene: boolean;
  hasProbe: boolean;
  /** Fractional move of the COMBINED light since the last upload. */
  drift: number;
  litLive: boolean;
  hasTrained: boolean;
}): LightDecision {
  if (state.hasScene || state.hasProbe) {
    const source: LightSource = state.hasScene && state.hasProbe
      ? "scene+probe" : state.hasScene ? "scene" : "probe";
    // The first live frame always uploads: `lastSh27` then holds the trained
    // light, so its drift answers a different question.
    return { upload: !state.litLive || state.drift >= RELIGHT_SH_DRIFT, source };
  }
  if (state.litLive) return { upload: true, source: state.hasTrained ? "trained" : "none" };
  return { upload: false, source: state.hasTrained ? "trained" : "none" };
}

/** Peak fractional change between two SH sets, scale-invariant the way
 *  `lightProbeSHDrift` is — the gate on re-uploading the light. */
export function sh27Drift(current: ArrayLike<number>, previous: ArrayLike<number>): number {
  let peak = 1e-6;
  let worst = 0;
  for (let i = 0; i < 27; i++) {
    const magnitude = Math.abs(Number(previous[i]));
    if (magnitude > peak) peak = magnitude;
    const delta = Math.abs(Number(current[i]) - Number(previous[i]));
    if (delta > worst) worst = delta;
  }
  return worst / peak;
}

/** Copy `src` into `out`, scaled. The SH is linear, so a gain on the
 *  coefficients is exactly a gain on the light. */
export function scaleSh27(out: Float32Array, src: ArrayLike<number>, gain: number): Float32Array {
  for (let i = 0; i < 27; i++) out[i] = Number(src[i]) * gain;
  return out;
}

/** `?pbrexposure=<gain>` on the PBR shade's light. 1 (the default) is the light
 *  as measured, so the parity gates are untouched.
 *
 *  It exists because the light a material was TRAINED under and the light of the
 *  room it is standing in are different quantities, and nothing reconciles them.
 *  A PBR branch follows the light and an RGB branch in the same bundle (myra's
 *  hair) cannot, so the two drift apart. Hair solves it with `shExposureNorm`;
 *  this is the explicit, non-normalising form.
 *
 *  IT SCALES THE COMBINED LIGHT, so the gain is NOT the probe's shortfall. The
 *  probe of a dim interior is 12-40x under a trained studio env — measured on
 *  MessyComputerLabOffice, 12.3/20.6/25.6x — but the scene's own key light is
 *  summed in first and carries most of the energy, leaving the total only ~3x
 *  down. Measured there: 1.75 matches the trained look and 8 is already clipped
 *  to pure white. Read the shortfall off `report().band0` against the bundle's
 *  own `pbr.env.sh27`, never off the probe alone. docs/OGS_PBR_PARITY.md §5g.
 *
 *  A malformed or non-positive value stays at 1 and says so — a typo must not
 *  silently relight the character. */
export function resolvePbrExposure(search: string, warn: (m: string) => void = console.warn): number {
  const raw = new URLSearchParams(search).get("pbrexposure");
  if (raw === null) return 1;
  const value = Number(raw);
  if (!Number.isFinite(value) || value <= 0) {
    warn(`[ogs] ?pbrexposure=${raw} is not a positive number — staying at 1`);
    return 1;
  }
  return value;
}
